import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Text from 'quarks/Text';
import { HeadingTypes, TextStyles } from 'quarks/styleProps/heading';

import AuthorCardDeck from 'components/CardDeck/AuthorCardDeck';
import BasicCardDeck from 'components/CardDeck/CardDeck';
import ChecklistCardDeck from 'components/CardDeck/ChecklistCardDeck';
import ComparisonCardDeck from 'components/CardDeck/ComparisonCardDeck';
import MinimalCardDeck from 'components/CardDeck/MinimalCardDeck';
import BlogCardDeck from 'components/CardDeck/ResourceCardDeck';
import ReviewCardDeck from 'components/CardDeck/ReviewCardDeck';
import StandardCardDeck from 'components/CardDeck/StandardCardDeck';
import TestimonialCardDeck from 'components/CardDeck/TestimonialCardDeck';

import { formatHeadingSize } from 'utils/formatHeadingSize';

import type { ContentfulComponentCardDeck } from 'graphqlTypes';
import type { FC } from 'react';

interface ComponentCardDeckProps extends ContentfulComponentCardDeck {
  backgroundColor?: string;
  backgroundIsDark: boolean;
  slider?: boolean;
}

const CardDeck: FC<ComponentCardDeckProps> = ({ resourceType, backgroundIsDark, slider, ...props }) => {
  const { heading, headingType, subheading } = props || {};
  const { headingSizeMobile, headingSizeTablet, headingSizeDesktop } = formatHeadingSize(props);

  const CardDeckComponent = () => {
    // Resource Type
    switch (resourceType) {
      case 'Basic':
        return <BasicCardDeck backgroundIsDark={backgroundIsDark} {...props} />;
      case 'Blog':
        return <BlogCardDeck backgroundIsDark={backgroundIsDark} {...props} pageType="Blog" />;
      case 'Case Study':
        return <BlogCardDeck backgroundIsDark={backgroundIsDark} {...props} pageType="Case Studies" />;
      case 'Author':
        return <AuthorCardDeck backgroundIsDark={backgroundIsDark} {...props} />;
      case 'Review':
        return <ReviewCardDeck backgroundIsDark={backgroundIsDark} {...props} />;
      case 'Comparison':
        return <ComparisonCardDeck backgroundIsDark={backgroundIsDark} {...props} />;
      case 'Testimonial':
        return <TestimonialCardDeck backgroundIsDark={backgroundIsDark} {...props} />;
      case 'Standard':
        return <StandardCardDeck backgroundIsDark={backgroundIsDark} {...props} />;
      case 'Minimal':
        return <MinimalCardDeck backgroundIsDark={backgroundIsDark} isSlider={slider} {...props} />;
      case 'Checklist':
        return <ChecklistCardDeck backgroundIsDark={backgroundIsDark} {...props} />;
      default:
        return null;
    }
  };

  return (
    <Flex marginX="auto" marginY={0} flexDirection="column" maxWidth="1170px">
      {(heading || subheading) && (
        <Flex paddingBottom={48} marginX="auto" textAlign="center" flexDirection="column" maxWidth="790px" gap="24px">
          {heading && (
            <Heading
              width="100%"
              as={(headingType?.toLowerCase() as HeadingTypes) || 'h2'}
              textStyle={(headingSizeMobile as TextStyles) || 'h3'}
              fontWeight="bold"
              textAlign="center"
              textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
              md={{ textStyle: (headingSizeTablet as TextStyles) || 'h3', fontWeight: 'bold' }}
              lg={{ textStyle: (headingSizeDesktop as TextStyles) || 'h2', fontWeight: 'bold' }}
            >
              {heading}
            </Heading>
          )}
          {subheading && (
            <Text
              width="100%"
              as="p"
              fontSize="textMd"
              alignSelf="center"
              textAlign="center"
              textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
            >
              {subheading.subheading}
            </Text>
          )}
        </Flex>
      )}
      <CardDeckComponent />
    </Flex>
  );
};

export default CardDeck;
